/**
 * .nulla file format – multi-section:
 *
 *   {"version":2}
 *   ===NULLA_TEXT===
 *   text content…
 *   ===NULLA_VIDEO===
 *   {"mime":"video/webm","duration":5.2,"voice":{"mime":"audio/webm","duration":5.1,"hex64":"<lowercase hex, 2 chars/byte>"}}
 *   <base64 string>
 *   ===NULLA_TEXT===
 *   more text…
 *   ===NULLA_VIDEO===
 *   {"mime":"video/webm","duration":10}
 *   <base64 string>
 *   ===NULLA_TEXT===
 *   final text…
 *   ===NULLA_AUDIO===
 *   {"mime":"audio/webm","duration":12.4}
 *   <lowercase hex, 2 chars/byte — same encoding as video meta.voice.hex64>
 *
 * Legacy v1 formats (NULLA_ABOVE/NULLA_VIDEO/NULLA_BELOW or NULLA_TEXT)
 * are auto-upgraded on read.
 */

const MARK_TEXT = "===NULLA_TEXT===";
const MARK_VIDEO = "===NULLA_VIDEO===";
const MARK_AUDIO = "===NULLA_AUDIO===";

/** Mic / narration track stored as lowercase hex (two nybbles per byte), not base64. */
export interface VoiceEnrichment {
  mime: string;
  duration: number;
  hex64: string;
}

export interface VideoMeta {
  mime: string;
  duration: number;
  /** Optional mic recording aligned with this video clip. */
  voice?: VoiceEnrichment;
}

/** Standalone audio block (no video container). */
export interface AudioMeta {
  mime: string;
  duration: number;
}

export type NullaSection =
  | { type: "text"; content: string }
  | { type: "video"; meta: VideoMeta; b64: string }
  | { type: "audio"; meta: AudioMeta; hex64: string };

/** Index of the last `video` section in buffer order, or -1 if none. */
export function lastVideoSectionIndex(sections: NullaSection[]): number {
  let last = -1;
  for (let i = 0; i < sections.length; i++) {
    if (sections[i]!.type === "video") last = i;
  }
  return last;
}

/** Index of the last `audio` section, or -1 if none. */
export function lastAudioSectionIndex(sections: NullaSection[]): number {
  let last = -1;
  for (let i = 0; i < sections.length; i++) {
    if (sections[i]!.type === "audio") last = i;
  }
  return last;
}

/** Last section index that can receive toolbar mic (last `video` or `audio` in order). */
export function lastMicTargetSectionIndex(sections: NullaSection[]): number {
  let last = -1;
  for (let i = 0; i < sections.length; i++) {
    const t = sections[i]!.type;
    if (t === "video" || t === "audio") last = i;
  }
  return last;
}

/** Last video section index in serialized Nulla v2 content; -1 if not Nulla or no video clip. */
export function lastNullaVideoContentSectionIndex(content: string): number {
  if (!content || !isNullaContent(content)) return -1;
  return lastVideoSectionIndex(parseSections(content));
}

/** Last mic-target section (video or audio) in serialized Nulla v2 content. */
export function lastNullaMicTargetContentSectionIndex(content: string): number {
  if (!content || !isNullaContent(content)) return -1;
  return lastMicTargetSectionIndex(parseSections(content));
}

/**
 * Minimal valid Nulla v2 document with one empty `NULLA_AUDIO` block (no hex payload).
 * Used for new tabs and toolbar mic without any video clip.
 */
export function minimalNullaDocumentWithPlaceholderAudio(): string {
  return encodeSections([
    { type: "text", content: "" },
    { type: "audio", meta: { mime: "audio/webm", duration: 0 }, hex64: "" },
    { type: "text", content: "" },
  ]);
}

/** @deprecated Prefer {@link minimalNullaDocumentWithPlaceholderAudio} (same behavior). */
export function minimalNullaDocumentWithPlaceholderClip(): string {
  return minimalNullaDocumentWithPlaceholderAudio();
}

/**
 * Ensures the buffer has at least one mic target (`video` or standalone `audio`).
 * - Empty / whitespace-only → {@link minimalNullaDocumentWithPlaceholderAudio}.
 * - Valid Nulla with no video and no audio → appends empty `NULLA_AUDIO` + trailing empty text.
 * - Non–Nulla-looking non-empty content → returned unchanged (caller must not overwrite).
 */
export function ensureNullaContentHasMicTargetForMic(content: string): string {
  const c = content ?? "";
  if (!c.trim()) return minimalNullaDocumentWithPlaceholderAudio();
  if (!isNullaContent(c)) return c;
  const sections = parseSections(c);
  if (lastMicTargetSectionIndex(sections) >= 0) return c;
  return encodeSections([
    ...sections,
    { type: "audio", meta: { mime: "audio/webm", duration: 0 }, hex64: "" },
    { type: "text", content: "" },
  ]);
}

/** @deprecated Use {@link ensureNullaContentHasMicTargetForMic}. */
export function ensureNullaContentHasVideoClipForMic(content: string): string {
  return ensureNullaContentHasMicTargetForMic(content);
}

/**
 * Appends an empty standalone `NULLA_AUDIO` block plus a trailing empty text section (v2 layout convention).
 * Used when starting a new toolbar mic capture so each recording gets its own section.
 */
export function appendEmptyNullaAudioSection(sections: NullaSection[]): {
  sections: NullaSection[];
  /** Index of the newly appended `audio` section in the returned list. */
  audioSectionIndex: number;
} {
  const next: NullaSection[] = [
    ...sections,
    { type: "audio", meta: { mime: "audio/webm", duration: 0 }, hex64: "" },
    { type: "text", content: "" },
  ];
  return { sections: next, audioSectionIndex: next.length - 2 };
}

/**
 * Removes the section at `index` if it is `video` or `audio`.
 * Returns `null` if the index is out of range or not a removable clip.
 */
export function removeNullaClipSectionAt(sections: NullaSection[], index: number): NullaSection[] | null {
  if (index < 0 || index >= sections.length) return null;
  const s = sections[index]!;
  if (s.type !== "video" && s.type !== "audio") return null;
  const out = sections.slice(0, index).concat(sections.slice(index + 1));
  if (out.length === 0) return [{ type: "text", content: "" }];
  return out;
}

/** Encode raw bytes as lowercase hexadecimal (field name in format: hex64). */
export function bytesToHex64(data: Uint8Array): string {
  let hex = "";
  for (let i = 0; i < data.length; i++) {
    hex += data[i]!.toString(16).padStart(2, "0");
  }
  return hex;
}

/** Decode {@link bytesToHex64} output; returns null if invalid. */
export function hex64ToBytes(hex: string): Uint8Array | null {
  const normalized = hex.trim().toLowerCase();
  if (normalized.length % 2 !== 0) return null;
  if (!/^[0-9a-f]*$/.test(normalized)) return null;
  const out = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export async function blobToVoiceEnrichment(blob: Blob, duration: number): Promise<VoiceEnrichment> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  return {
    mime: blob.type || "audio/webm",
    duration: Math.round(duration * 10) / 10,
    hex64: bytesToHex64(bytes),
  };
}

// ─── Detection / Header ────────────────────────────────────────────

/** Strip UTF-8 BOM so `{"version":2}` files saved from Windows tools still detect as Nulla. */
export function stripLeadingUtf8Bom(content: string): string {
  return content.replace(/^\uFEFF/, "");
}

export function isNullaContent(content: string): boolean {
  const s = stripLeadingUtf8Bom(content);
  if (!s.startsWith("{")) return false;
  const nl = s.indexOf("\n");
  if (nl < 0) return false;
  try {
    const h = JSON.parse(s.slice(0, nl)) as Record<string, unknown>;
    const v = h.version;
    return (v === 1 || v === 2) && (typeof h.mime === "string" || v === 2);
  } catch {
    return false;
  }
}

export function isNullaFilename(name: string): boolean {
  return name.toLowerCase().endsWith(".nulla");
}

// ─── Parsing ────────────────────────────────────────────────────────

export function parseSections(content: string): NullaSection[] {
  if (!isNullaContent(content)) return [{ type: "text", content: "" }];
  const s = stripLeadingUtf8Bom(content);
  const nl = s.indexOf("\n");
  const headerLine = s.slice(0, nl);
  let hdr: Record<string, unknown>;
  try {
    hdr = JSON.parse(headerLine) as Record<string, unknown>;
  } catch {
    return [{ type: "text", content: "" }];
  }

  const body = s.slice(nl + 1);

  if (hdr.version === 1) return parseLegacyBody(hdr, body);
  return parseV2Body(body);
}

function parseV2Body(body: string): NullaSection[] {
  const lines = body.split("\n");
  const sections: NullaSection[] = [];
  let i = 0;

  while (i < lines.length) {
    if (lines[i] === MARK_TEXT) {
      i++;
      const start = i;
      while (
        i < lines.length &&
        lines[i] !== MARK_TEXT &&
        lines[i] !== MARK_VIDEO &&
        lines[i] !== MARK_AUDIO
      ) {
        i++;
      }
      sections.push({ type: "text", content: lines.slice(start, i).join("\n") });
    } else if (lines[i] === MARK_VIDEO) {
      i++;
      let meta: VideoMeta = { mime: "video/webm", duration: 0 };
      if (i < lines.length && lines[i]!.startsWith("{")) {
        try {
          const parsed = JSON.parse(lines[i]!) as Record<string, unknown>;
          meta = {
            mime: typeof parsed.mime === "string" ? parsed.mime : "video/webm",
            duration: typeof parsed.duration === "number" ? parsed.duration : 0,
          };
          const v = parsed.voice;
          if (v && typeof v === "object" && v !== null) {
            const vo = v as Record<string, unknown>;
            const hex64 = typeof vo.hex64 === "string" ? vo.hex64.trim() : "";
            if (hex64.length > 0 && hex64.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(hex64)) {
              meta.voice = {
                mime: typeof vo.mime === "string" ? vo.mime : "audio/webm",
                duration: typeof vo.duration === "number" ? vo.duration : meta.duration,
                hex64: hex64.toLowerCase(),
              };
            }
          }
        } catch { /* use defaults */ }
        i++;
      }
      const start = i;
      while (
        i < lines.length &&
        lines[i] !== MARK_TEXT &&
        lines[i] !== MARK_VIDEO &&
        lines[i] !== MARK_AUDIO
      ) {
        i++;
      }
      sections.push({ type: "video", meta, b64: lines.slice(start, i).join("\n") });
    } else if (lines[i] === MARK_AUDIO) {
      i++;
      let meta: AudioMeta = { mime: "audio/webm", duration: 0 };
      if (i < lines.length && lines[i]!.startsWith("{")) {
        try {
          const parsed = JSON.parse(lines[i]!) as Record<string, unknown>;
          meta = {
            mime: typeof parsed.mime === "string" ? parsed.mime : "audio/webm",
            duration: typeof parsed.duration === "number" ? parsed.duration : 0,
          };
        } catch { /* use defaults */ }
        i++;
      }
      const start = i;
      while (
        i < lines.length &&
        lines[i] !== MARK_TEXT &&
        lines[i] !== MARK_VIDEO &&
        lines[i] !== MARK_AUDIO
      ) {
        i++;
      }
      const hexJoined = lines.slice(start, i).join("").replace(/\s+/g, "").toLowerCase();
      let hex64 = hexJoined;
      if (hex64.length > 0 && (hex64.length % 2 !== 0 || !/^[0-9a-f]+$/.test(hex64))) {
        hex64 = "";
      }
      sections.push({ type: "audio", meta, hex64 });
    } else {
      i++;
    }
  }

  if (sections.length === 0) sections.push({ type: "text", content: "" });
  return sections;
}

function parseLegacyBody(hdr: Record<string, unknown>, body: string): NullaSection[] {
  const lines = body.split("\n");
  const mime = typeof hdr.mime === "string" ? hdr.mime : "video/webm";
  const duration = typeof hdr.duration === "number" ? hdr.duration : 0;

  // v1 new format: NULLA_ABOVE / NULLA_VIDEO / NULLA_BELOW
  const aboveIdx = lines.indexOf("===NULLA_ABOVE===");
  const videoIdx = lines.indexOf("===NULLA_VIDEO===");
  const belowIdx = lines.indexOf("===NULLA_BELOW===");

  if (videoIdx >= 0) {
    const aboveStart = aboveIdx >= 0 ? aboveIdx + 1 : videoIdx;
    const above = lines.slice(aboveStart, videoIdx).join("\n");
    const videoEnd = belowIdx >= 0 ? belowIdx : lines.length;
    const b64 = lines.slice(videoIdx + 1, videoEnd).join("\n");
    const below = belowIdx >= 0 ? lines.slice(belowIdx + 1).join("\n") : "";
    return [
      { type: "text", content: above },
      { type: "video", meta: { mime, duration }, b64 },
      { type: "text", content: below },
    ];
  }

  // v1 oldest format: just base64 (optionally with ===NULLA_TEXT===)
  const oldSepIdx = lines.indexOf("===NULLA_TEXT===");
  if (oldSepIdx >= 0) {
    return [
      { type: "text", content: "" },
      { type: "video", meta: { mime, duration }, b64: lines.slice(0, oldSepIdx).join("\n") },
      { type: "text", content: lines.slice(oldSepIdx + 1).join("\n") },
    ];
  }

  return [
    { type: "text", content: "" },
    { type: "video", meta: { mime, duration }, b64: body },
    { type: "text", content: "" },
  ];
}

// ─── Encoding ───────────────────────────────────────────────────────

export function encodeSections(sections: NullaSection[]): string {
  const headerLine = JSON.stringify({ version: 2 });
  const parts: string[] = [headerLine];

  for (const s of sections) {
    if (s.type === "text") {
      parts.push(MARK_TEXT, s.content);
    } else if (s.type === "video") {
      parts.push(MARK_VIDEO, JSON.stringify(s.meta), s.b64);
    } else {
      parts.push(MARK_AUDIO, JSON.stringify(s.meta), s.hex64);
    }
  }

  return parts.join("\n");
}

// ─── Convenience ────────────────────────────────────────────────────

/** Decode all video blobs in a section array (indices match). */
export function decodeVideoBlobs(sections: NullaSection[]): (Blob | null)[] {
  return sections.map((s) => {
    if (s.type !== "video") return null;
    try {
      const binary = atob(s.b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: s.meta.mime });
    } catch {
      return null;
    }
  });
}

/** Encode a Blob to base64, returning a video section. */
export async function blobToVideoSection(
  blob: Blob,
  duration: number,
): Promise<Extract<NullaSection, { type: "video" }>> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return {
    type: "video",
    meta: { mime: blob.type || "video/webm", duration: Math.round(duration * 10) / 10 },
    b64: btoa(binary),
  };
}

/** Get all text section values in order. */
export function getTextSections(sections: NullaSection[]): string[] {
  return sections.filter((s): s is NullaSection & { type: "text" } => s.type === "text").map((s) => s.content);
}

/** Update all text sections from an array of values (order-preserving). */
export function setTextSections(sections: NullaSection[], values: string[]): NullaSection[] {
  let ti = 0;
  return sections.map((s) => {
    if (s.type === "text") {
      return { ...s, content: values[ti++] ?? "" };
    }
    return s;
  });
}

/** Insert a new video section after the text section at textIndex, followed by a new empty text section. */
export function insertVideoAfterText(
  sections: NullaSection[],
  textIndex: number,
  videoSection: NullaSection & { type: "video" },
): NullaSection[] {
  let ti = 0;
  let insertAfter = -1;
  for (let i = 0; i < sections.length; i++) {
    if (sections[i]!.type === "text") {
      if (ti === textIndex) { insertAfter = i; break; }
      ti++;
    }
  }
  if (insertAfter < 0) insertAfter = sections.length - 1;
  const result = [...sections];
  result.splice(insertAfter + 1, 0, videoSection, { type: "text", content: "" });
  return result;
}

// ─── Legacy compat shims (parseNullaHeader for status line) ─────────

export interface NullaHeader {
  version: number;
  mime: string;
  duration: number;
}

export function parseNullaHeader(content: string): NullaHeader | null {
  const nl = content.indexOf("\n");
  if (nl < 0) return null;
  try {
    const h = JSON.parse(content.slice(0, nl)) as Record<string, unknown>;
    if (h.version === 2) {
      const sections = parseSections(content);
      const firstVideo = sections.find((s): s is NullaSection & { type: "video" } => s.type === "video");
      if (firstVideo) {
        return { version: 2, mime: firstVideo.meta.mime, duration: firstVideo.meta.duration };
      }
      const firstAudio = sections.find((s): s is NullaSection & { type: "audio" } => s.type === "audio");
      if (firstAudio) {
        return { version: 2, mime: firstAudio.meta.mime, duration: firstAudio.meta.duration };
      }
      return null;
    }
    if (h.version !== 1 || typeof h.mime !== "string") return null;
    return { version: 1, mime: h.mime as string, duration: (h.duration as number) ?? 0 };
  } catch {
    return null;
  }
}
